import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

export interface AvailabilityData {
  status: 'available' | 'unavailable' | 'partial';
  notes?: string;
  timeSlots?: { start: string; end: string }[];
  date: string;
  updatedAt: string;
}

export interface Availability {
  [date: string]: AvailabilityData;
}

const API_URL = process.env.REACT_APP_API_URL || '';

export function useAvailability() {
  const { token } = useAuth();
  const [availability, setAvailability] = useState<Availability>({});
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const socketRef = useRef<Socket | null>(null);

  // Fetch initial availability
  const fetchAvailability = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Setup socket connection
  useEffect(() => {
    if (!token) return;

    const socketUrl = API_URL || window.location.origin;
    const socket = io(socketUrl);
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socket.on('initial-availability', (data: Availability) => {
      setAvailability(data);
      setIsLoading(false);
    });

    socket.on('availability-update', ({ date, data }: { date: string; data: AvailabilityData }) => {
      setAvailability(prev => {
        if (data.status === 'unavailable') {
          const { [date]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [date]: data };
      });
    });

    socket.on('availability-bulk-update', (data: Availability) => {
      setAvailability(data);
    });

    // Also fetch via API for reliability
    fetchAvailability();

    return () => {
      socket.disconnect();
    };
  }, [token, fetchAvailability]);

  // Update availability (admin only)
  const updateAvailability = async (
    date: string,
    status: 'available' | 'unavailable' | 'partial',
    notes?: string,
    timeSlots?: { start: string; end: string }[]
  ) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/availability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ date, status, notes, timeSlots })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update');
    }

    return response.json();
  };

  // Bulk update availability for multiple dates (admin only)
  const bulkUpdateAvailability = async (
    dates: string[],
    status: 'available' | 'unavailable' | 'partial',
    notes?: string,
    timeSlots?: { start: string; end: string }[]
  ) => {
    if (!token) return;

    const dateUpdates = dates.map(date => ({
      date,
      status,
      notes,
      timeSlots
    }));

    const response = await fetch(`${API_URL}/api/availability/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ dates: dateUpdates })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update');
    }

    return response.json();
  };

  const getDateStatus = (date: string): AvailabilityData | null => {
    return availability[date] || null;
  };

  return {
    availability,
    isConnected,
    isLoading,
    updateAvailability,
    bulkUpdateAvailability,
    getDateStatus,
    refetch: fetchAvailability
  };
}
